/**
 * @class Overlay
 */
class Overlay {

  /**
   * Singleton instance to ensure only one overlay is present
   * @constructor 
   */

  constructor() {
    if (Overlay.instance) return Overlay.instance; // Return the existing instance if already created
    Overlay.instance = this;
  }

  /**
   * Creates the overlay element with the specified content.
   * @param {string|HTMLElement} content - HTML content string or DOM element to be displayed inside the overlay.
   */
  #createOverlay(content) {
    const overlay = document.createElement("div");
    overlay.id = "overlay";
    overlay.classList.add("overlay");
    
    // Check if content is a DOM element or HTML string
    if (content instanceof HTMLElement) {
      // If it's a DOM element, append it directly
      overlay.appendChild(content);
    } else {
      // If it's a string, use innerHTML like before
      overlay.innerHTML = content;
    }
    
    document.body.appendChild(overlay);

    // Adding the 'show' class to trigger the overlay's appearance with a slight delay
    setTimeout(() => {
      overlay.classList.add("show");
      document.body.classList.add("no-scroll");
    }, 10);
  }

  /**
   * Displays the overlay with the provided message.
   * If an overlay already exists, it will simply show it.
   * @param {string|HTMLElement} message - The content to be displayed inside the overlay (HTML string or DOM element).
   */
  showOverlay(message) {
    const existingOverlay = document.getElementById("overlay");

    if (!existingOverlay) {
      // If no overlay exists, create a new one
      this.#createOverlay(message);
    } else {
      // If an overlay exists, update its content and show it
      if (message instanceof HTMLElement) {
        // Clear existing content and append the new DOM element
        existingOverlay.innerHTML = '';
        existingOverlay.appendChild(message);
      } else {
        // Update with HTML string
        existingOverlay.innerHTML = message;
      }
      existingOverlay.classList.add("show");
      document.body.classList.add("no-scroll");
    }
  }

  /**
   * Closes and removes the overlay from the DOM.
   * Optionally reloads the page after closing the overlay.
   * @param {boolean} reload - Whether to reload the page after closing the overlay.
   */
  closeOverlay(reload = false) {
    const overlay = document.getElementById("overlay");

    if (overlay) {
      overlay.classList.remove("show");

      // CRITICAL FIX: Remove overlay immediately to allow routing
      // The fade-out animation will still play via CSS, but DOM element is removed
      // This prevents the router from detecting overlay and blocking navigation
      overlay.remove();
      document.body.classList.remove("no-scroll");

      if (reload) {
        window.location.href = "/";
      }
    }
  }
}

// Create and export the singleton instance
const overlay = new Overlay();
export default overlay;